import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControlLabel, Checkbox, Typography, Box, IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const CreditWaiverDialog = ({ open, onClose, onConfirm }) => {
  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [error, setError] = useState("");

  // Sub-dialog states for details
  const [subDoc, setSubDoc] = useState(null); // 'waiver' or 'gradeMatch' or null

  const handleSubmit = () => {
    if (!ack1 || !ack2) {
      setError("You must acknowledge both credit waiver statements to proceed.");
      return;
    }
    setError("");
    setAck1(false);
    setAck2(false);
    onConfirm();
  };

  const handleClose = () => {
    setAck1(false);
    setAck2(false);
    setError("");
    onClose();
  };

  const openSubDialog = (docType) => {
    setSubDoc(docType);
  };

  const closeSubDialog = (agreed) => {
    if (subDoc === "waiver") {
      setAck1(!!agreed);
    } else if (subDoc === "gradeMatch") {
      setAck2(!!agreed);
    }
    setSubDoc(null);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" sx={{ zIndex: 999999999 }} fullWidth>
        <DialogTitle sx={{ fontWeight: "bold", color: "#1a237e" }}>
          Credit Waiver Confirmation
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Students enrolling in Nilai 6-8 are required to acknowledge the following
            credit waiver statements before proceeding with registration.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={ack1}
                  onChange={(e) => {
                    if (ack1) {
                      setAck1(false);
                    } else {
                      openSubDialog("waiver");
                    }
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    openSubDialog("waiver");
                  }}
                  sx={{ color: "#1a237e", textDecoration: "underline", cursor: "pointer" }}
                >
                  High School Credit Waiver Form
                </Typography>
              }
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={ack2}
                  onChange={(e) => {
                    if (ack2) {
                      setAck2(false);
                    } else {
                      openSubDialog("gradeMatch");
                    }
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    openSubDialog("gradeMatch");
                  }}
                  sx={{ color: "#1a237e", textDecoration: "underline", cursor: "pointer" }}
                >
                  Grade Match Waiver Form
                </Typography>
              }
            />
          </Box>

          {error && (
            <Typography color="error" variant="body2" mt={1}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained"
            sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)" }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sub-Dialog showing the detailed documents */}
      <Dialog open={!!subDoc} onClose={() => closeSubDialog(false)} maxWidth="sm" sx={{ zIndex: 999999999 }} fullWidth>
        <DialogTitle sx={{ fontWeight: "bold", color: "#1a237e", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {subDoc === "waiver" ? "High School Credit Waiver" : "Grade Match Waiver"}
          <IconButton onClick={() => closeSubDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {subDoc === "waiver" && (
            <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: "pre-line" }}>
              By signing this waiver, I acknowledge and understand that I have specifically requested my child to be placed in the desired grade at Cumming Tamil School, fully aware that successful graduation from this grade will not make my child eligible for High School Credit.
            </Typography>
          )}
          {subDoc === "gradeMatch" && (
            <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: "pre-line" }}>
              Cumming Tamil School recommends that students be placed one Nilai below their public school grade level <br /><br /> to ensure eligibility for Tamil language credit in future academic years.
              <br /><br />
              Credit eligibility policies have changed in the past and may change again at the discretion of the county. Cumming Tamil School cannot be held responsible for any future changes in county policy that may affect credit recognition. Parents who choose to request a grade match acknowledge this risk and agree to proceed with the understanding that their child’s placement may impact future credit eligibility.
              <br />
              {"\n"}By requesting a grade match: <br />
              - I acknowledge that county policies regarding Tamil language credit may change at any time.<br />
              - I understand that this placement may impact future credit eligibility.<br />
              - I accept that the school will not be responsible for any policy changes that affect credit recognition.<br />
              - I agree to pay a $50 book fee if books for the previous grade have already been ordered.<br />

              {"\n"}I/We hereby acknowledge that both parents of the child/children have read, understood and agree to the terms and conditions mentioned in the above agreement. By checking here, you are consenting to the use of your waiver as equivalent to electronic signature and also in lieu of an original signature on paper.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => closeSubDialog(false)} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button onClick={() => closeSubDialog(true)} variant="contained" sx={{ bgcolor: "#244C77", "&:hover": { bgcolor: "#1a237e" } }}>
            I Agree
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreditWaiverDialog;
